import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import EventCard from "@/components/feature/EventCard";
import FilterPanel, { type FilterValues } from "@/pages/category/components/FilterPanel";
import Pagination from "@/pages/category/components/Pagination";
import { filterEvents, sortEvents } from "@/utils/events";
import { setPageSeo } from "@/utils/seo";

const brandSuffix = "51每日大赛今日黑聚集地详情";

const dateRanges = ["全部时间", "近 3 天", "近 7 天", "近 15 天", "近 30 天"];
const pageSize = 6;

const sortOptions = [
  { key: "time", label: "按时间排序", icon: "ri-time-line" },
  { key: "heat", label: "按热度排序", icon: "ri-fire-line" },
  { key: "name", label: "按名称排序", icon: "ri-sort-alphabet-asc" },
];

export default function Category() {
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<FilterValues>({
    category: searchParams.get("category") || "全部类型",
    region: "全部地区",
    dateRange: "全部时间",
    status: "全部状态",
    tags: [],
  });
  const [keyword, setKeyword] = useState(searchParams.get("q") || "");
  const [sortBy, setSortBy] = useState("time");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const patchFilters = (patch: Partial<FilterValues>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      category: "全部类型",
      region: "全部地区",
      dateRange: "全部时间",
      status: "全部状态",
      tags: [],
    });
    setKeyword("");
    setPage(1);
  };

  useEffect(() => {
    const name = filters.category !== "全部类型" ? `${filters.category}赛事` : "赛事分类";
    setPageSeo({
      title: `${name} - ${brandSuffix}`,
      description: `${name}列表汇总了近期每日大赛赛程与对阵信息，可按类型、地区、时间与热门标签筛选，快速查看赛事详情与比赛介绍。`,
      keywords: `${filters.category},赛事分类,赛程安排,比赛资讯,${brandSuffix}`,
    });
  }, [filters.category]);

  const filtered = useMemo(
    () => filterEvents({ ...filters, keyword }),
    [filters, keyword]
  );
  const sorted = useMemo(() => sortEvents(filtered, sortBy), [filtered, sortBy]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6">
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">赛事分类</span>
      </nav>

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          赛事分类列表
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          按赛事类型、地区、举办时间与热门标签筛选浏览全部赛事。赛事覆盖电子竞技、体育竞技、棋牌竞技与极限运动，支持按时间、热度与名称排序，帮助你快速定位感兴趣的赛程与对阵信息。
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-28">
            <FilterPanel
              values={filters}
              dateRanges={dateRanges}
              onChange={patchFilters}
              onReset={resetFilters}
            />
          </div>
        </aside>

        <div>
          <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 lg:hidden"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-bold text-accent-600">{sorted.length}</span> 场赛事
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setSortBy(option.key);
                    setPage(1);
                  }}
                  className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                    sortBy === option.key
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <i className={option.icon}></i>
                  {option.label.replace("排序", "")}
                </button>
              ))}
            </div>
          </div>

          {keyword && (
            <div className="mt-4 flex items-center gap-2 text-sm text-foreground-600">
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-800">
                关键词：{keyword}
              </span>
              <button
                type="button"
                onClick={() => setKeyword("")}
                className="text-xs text-foreground-500 hover:text-accent-600"
              >
                清除
              </button>
            </div>
          )}

          {paged.length > 0 ? (
            <>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {paged.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                暂无匹配赛事
              </h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                当前筛选条件下没有找到相关赛事，可尝试调整筛选条件或重置后重新浏览。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm overflow-y-auto bg-background-50 p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭筛选"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel
              values={filters}
              dateRanges={dateRanges}
              onChange={patchFilters}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50"
            >
              查看 {sorted.length} 场赛事
            </button>
          </div>
        </div>
      )}
    </div>
  );
}